import { isSkorSdkMode } from "../../../helper/skorSdk";
import PoweredBySkor from "../PoweredBySkor/PoweredBySkor";
import type { ReactNode } from "react";
import useTranslator from "../../../hooks/useTranslator";
import SidePanel from "../SidePanel/SidePanel";
import SkorLogo from "../SkorLogo/SkorLogo";
import StepBars from "../StepBars/StepBars";
import Icon from "../Icon/Icon";
import "../../../pages/onboarding/onboardingProgress.css";

export interface ProgressRailProps {
  /** label for screen readers, for example "Your process" */
  label: string;
  /** the steps, used for the bottom bars and the phone header */
  steps: { id: string; title: ReactNode }[];
  activeIndex: number;
  /** middle of the panel: eyebrow text, StepList, PeopleList */
  children: ReactNode;
  /** language switch, shown in the phone header */
  language?: ReactNode;
  /** extra block under the steps, for example a company logo */
  extra?: ReactNode;
  className?: string;
}

/**
 * The dark progress side bar on sign-up, onboarding, guarantor, co-tenant
 * and tenant-check pages. On phones it turns into a top header with dots.
 * Built on SidePanel, so background and lines match the login pages.
 */
const ProgressRail = ({
  label,
  steps,
  activeIndex,
  children,
  language,
  extra,
  className = "",
}: ProgressRailProps) => {
  const { _t } = useTranslator();
  const total = steps.length;
  const current = Math.min(Math.max(activeIndex, 0), Math.max(total - 1, 0));
  const activeStep = steps[current];

  return (
    <SidePanel
      logo={false}
      className={`onboarding-progress ${className}`.trim()}
      aria-label={label}
    >
      {language && (
        <div
          className="onboarding-progress__language"
          aria-label="language switch"
        >
          {language}
        </div>
      )}

      <div className="onboarding-progress__content">
        {isSkorSdkMode() ? (
          <div className="onboarding-progress__logo">
            <PoweredBySkor />
          </div>
        ) : (
          <SkorLogo className="onboarding-progress__logo" />
        )}
        <div className="onboarding-progress__main">{children}</div>
        {extra}

        <StepBars
          className="onboarding-progress__foot"
          total={total}
          current={current + 1}
          caption={
            <>
              {_t("stepCurrentOfTotal", "", "translation", {
                current: current + 1,
                total,
              })}
              {activeStep && <> · {activeStep.title}</>}
            </>
          }
        />

        <div className="onboarding-progress__mobile">
          {isSkorSdkMode() ? (
            <PoweredBySkor className="onboarding-progress__mobile-logo" />
          ) : (
            <SkorLogo className="onboarding-progress__mobile-logo" />
          )}
          <div className="onboarding-progress__mobile-process">
            <div
              className="onboarding-progress__mobile-track"
              style={{ left: `${50 / total}%`, right: `${50 / total}%` }}
              aria-hidden="true"
            >
              <span
                style={{
                  width: `${total > 1 ? (current / (total - 1)) * 100 : 0}%`,
                }}
              />
            </div>
            <ol
              className="onboarding-progress__mobile-steps"
              style={{ gridTemplateColumns: `repeat(${total}, 1fr)` }}
            >
              {steps.map((step, index) => (
                <li
                  key={step.id}
                  className={`${index === current ? "is-active" : ""} ${
                    index < current ? "is-complete" : ""
                  }`}
                  aria-current={index === current ? "step" : undefined}
                >
                  <span>
                    {index < current ? (
                      <Icon name="check" size={14} />
                    ) : (
                      <span aria-hidden="true" />
                    )}
                  </span>
                </li>
              ))}
            </ol>
            <div
              className="onboarding-progress__mobile-label"
              style={{ gridTemplateColumns: `repeat(${total}, 1fr)` }}
              aria-live="polite"
            >
              <strong style={{ gridColumn: current + 1 }}>
                {activeStep?.title}
              </strong>
            </div>
          </div>
        </div>
      </div>
    </SidePanel>
  );
};

export default ProgressRail;
